import { Box, Button, Chip, FormControl, FormLabel, IconButton, Input, Option, Select, Sheet, Stack, Typography, CircularProgress, Alert } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import { LayoutContext } from "../context";
import { APIGetCoverageZones, APICreateCoverageZone, APIDeleteCoverageZone } from "../api";

/**
 * Универсальный редактор зон покрытия.
 *
 * Props:
 *   employeeId        — обязательный, ID исполнителя
 *   token             — токен авторизации (передавать tokenEmployee)
 *   highlightRequired — bool, если true — рамка предупреждения, когда зон 0
 *   onChange          — callback (zones[]) => void; вызывается при изменении списка
 *   readOnly          — bool, режим просмотра (без кнопок добавления/удаления)
 *   compact           — bool, более плотная вёрстка для модалки в профиле
 */
export default function CoverageZonesEditor({
    employeeId,
    token,
    highlightRequired = false,
    onChange,
    readOnly = false,
    compact = false,
}) {
    const { citys } = useContext(LayoutContext);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);

    // Форма добавления
    const [newCity, setNewCity] = useState(null);

    const reload = async () => {
        if (!employeeId) return;
        setLoading(true);
        const list = await APIGetCoverageZones(employeeId, token);
        setZones(Array.isArray(list) ? list : []);
        setLoading(false);
        onChange?.(Array.isArray(list) ? list : []);
    };

    useEffect(() => { reload(); }, [employeeId, token]);

    const handleAdd = async () => {
        if (!newCity) {
            alert('Выберите город');
            return;
        }
        setAdding(true);
        const result = await APICreateCoverageZone({
            employee: employeeId,
            city_bitrix: newCity,
            is_active: true,
            priority: 0,
        }, token);
        if (result?.status !== 'error') {
            setNewCity(null);
            await reload();
        } else {
            alert('Не удалось добавить зону');
        }
        setAdding(false);
    };

    const handleDelete = async (zoneId) => {
        if (!window.confirm('Удалить зону?')) return;
        await APIDeleteCoverageZone(zoneId, token);
        await reload();
    };

    const isEmpty = zones.length === 0 && !loading;
    const showAlert = highlightRequired && isEmpty;

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <MapRoundedIcon sx={{ color: showAlert ? '#ef4444' : '#6366f1' }} />
                <Typography level="title-sm" sx={{ color: showAlert ? 'danger.600' : 'text.primary' }}>
                    Зоны покрытия {showAlert && '(обязательно)'}
                </Typography>
                {!loading && <Chip size="sm" variant="soft">{zones.length}</Chip>}
            </Stack>

            {showAlert && (
                <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                    {readOnly
                        ? 'Города не заданы — приглашения на новые заявки приходить не будут. Обратитесь к менеджеру, чтобы вам настроили зоны покрытия.'
                        : 'Укажите хотя бы один город, чтобы получать заявки. Без зон покрытия приглашения на новые заявки приходить не будут.'}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size="sm" />
                </Box>
            ) : (
                <Stack spacing={0.75}>
                    {zones.map(z => (
                        <Sheet
                            key={z.id}
                            variant="outlined"
                            sx={{
                                p: compact ? 1 : 1.25,
                                borderRadius: 'lg',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip size="sm" variant="soft" color="primary">
                                    {citys?.find(c => c.id === z.city_bitrix)?.name
                                        || z.city_name
                                        || `Город #${z.city_bitrix}`}
                                </Chip>
                                {!z.is_active && (
                                    <Chip size="sm" variant="soft" color="warning">Выключена</Chip>
                                )}
                            </Stack>
                            {!readOnly && (
                                <IconButton size="sm" color="danger" variant="plain"
                                    onClick={() => handleDelete(z.id)}>
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Sheet>
                    ))}

                    {zones.length === 0 && (
                        <Typography level="body-sm" color="neutral" sx={{ fontStyle: 'italic' }}>
                            {readOnly ? 'Города не назначены' : 'Зон пока нет'}
                        </Typography>
                    )}

                    {readOnly && zones.length > 0 && (
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            Изменить список городов может менеджер.
                        </Typography>
                    )}
                </Stack>
            )}

            {!readOnly && (
                <Sheet
                    variant="outlined"
                    sx={{
                        p: 1.5, mt: 1.5, borderRadius: 'lg',
                        border: '1px dashed', borderColor: 'divider',
                    }}
                >
                    <Typography level="body-xs" sx={{ fontWeight: 700, mb: 1, color: 'text.tertiary' }}>
                        Добавить зону
                    </Typography>
                    <Stack spacing={1}>
                        <FormControl size="sm" required>
                            <FormLabel sx={{ fontSize: '0.75rem' }}>Город, в котором работаете</FormLabel>
                            <Select
                                size="sm"
                                placeholder="Выберите город"
                                value={newCity}
                                onChange={(_, v) => setNewCity(v)}
                                slotProps={{ listbox: { sx: { zIndex: 100020 } } }}
                            >
                                {citys?.sort((a, b) => a.name.localeCompare(b.name, 'ru'))?.map(c => (
                                    <Option key={c.id} value={c.id}>{c.name}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            size="sm" color="primary" variant="soft"
                            startDecorator={<AddRoundedIcon />}
                            loading={adding}
                            onClick={handleAdd}
                            disabled={!newCity}
                        >
                            Добавить город
                        </Button>
                    </Stack>
                </Sheet>
            )}
        </Box>
    );
}